/**
 * Contact Masking Utility
 * Cameroon Land Market Platform
 * 
 * Standardizes partial masking of sensitive phone numbers and email addresses
 * across client, server, and static rendering to protect seller privacy and
 * prevent automated scraping.
 */

/**
 * Partially masks a phone number.
 * Examples:
 *   "+237 677 12 34 56" -> "+237 67* *** *56"
 *   "677123456"         -> "+237 67* *** *56"
 *   "237677123456"      -> "+237 67* *** *56"
 *   "0551234589"        -> "0551****89"
 *   "+49 151 234 567 89"-> "+49 151 *** ** 89"
 */
export function maskPhoneNumber(phone?: string | null): string {
  if (!phone) return '';
  const trimmed = phone.trim();
  if (trimmed.length < 5) return '***';

  // If already masked (contains multiple stars), return as-is
  if ((trimmed.match(/\*/g) || []).length >= 2) return trimmed;

  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');

  if (digits.length <= 4) {
    return '***';
  }

  // Cameroon 9-digit local format: e.g. 677 12 34 56 -> +237 67* *** *56
  if (digits.length === 9) {
    const operator = digits.slice(0, 2); // 67, 69, 65, 22, etc.
    const last2 = digits.slice(-2);
    return `+237 ${operator}* *** *${last2}`;
  }

  // Cameroon 12-digit international format: 237677123456 -> +237 67* *** *56
  if (digits.length === 12 && digits.startsWith('237')) {
    const operator = digits.slice(3, 5); // 67
    const last2 = digits.slice(-2);
    return `+237 ${operator}* *** *${last2}`;
  }

  // German / French / European international: e.g. +49 151 ... 89
  if (trimmed.includes(' ') || hasPlus) {
    const parts = trimmed.split(/\s+/);
    if (parts.length >= 3) {
      const countryOrPrefix = parts.slice(0, 2).join(' ');
      const lastPart = parts[parts.length - 1];
      const suffix = lastPart.slice(-2);
      return `${countryOrPrefix} *** ** ${suffix}`;
    }
  }

  // Standard national or raw digits: keep prefix (up to 4 chars), mask middle, keep last 2
  const prefixLength = digits.length > 8 ? 4 : 3;
  const suffixLength = 2;
  const prefix = digits.slice(0, prefixLength);
  const suffix = digits.slice(-suffixLength);
  const starsCount = Math.max(4, digits.length - prefixLength - suffixLength);

  return `${hasPlus ? '+' : ''}${prefix}${'*'.repeat(starsCount)}${suffix}`;
}

/**
 * Partially masks an email address.
 * Examples:
 *   "jean.dupont@gmail.com" -> "j***@gmail.com"
 *   "contact@cameroonlandmarket.com" -> "c***@cameroonlandmarket.com"
 *   "a@domain.com" -> "a***@domain.com"
 */
export function maskEmail(email?: string | null): string {
  if (!email) return '';
  const trimmed = email.trim().toLowerCase();
  if (!trimmed.includes('@')) return '***@***.***';

  // If already masked
  if (trimmed.includes('***')) return trimmed;

  const [localPart, domain] = trimmed.split('@');
  if (!localPart || !domain) return '***@***.***';

  const firstChar = localPart.charAt(0);
  return `${firstChar}***@${domain}`;
}

/**
 * Checks if a string is already masked.
 */
export function isContactMasked(value?: string | null): boolean {
  if (!value) return false;
  return value.includes('*');
}
